import type { ComponentProps } from "react"
import { useState } from "react"
import { HugeiconsIcon } from "@hugeicons/react"
import { ViewIcon, ViewOffSlashIcon } from "@hugeicons/core-free-icons"
import { InputGroupButton, cn } from "@motley/design"
import FormInput from "./FormInput"

type Props = Omit<
  ComponentProps<typeof FormInput>,
  | "type"
  | "autoComplete"
  | "autoCapitalize"
  | "autoCorrect"
  | "spellCheck"
  | "icon"
>

/**
 * Input for values the browser must never retain — provider API keys, env var
 * values. Stays `type="text"` on purpose: `type="password"` is what triggers
 * the "Save password?" prompt, so masking is done in CSS instead.
 */
export default function FormSecretField({ inputClassName, ...props }: Props) {
  const [revealed, setRevealed] = useState(false)

  return (
    <FormInput
      {...props}
      type="text"
      // `off` covers both the autofill dropdown and value restoration on reload
      autoComplete="off"
      autoCapitalize="off"
      autoCorrect="off"
      // Enhanced spellcheck and extensions ship field contents to remote servers
      spellCheck={false}
      inputClassName={cn(
        !revealed && "[-webkit-text-security:disc]",
        inputClassName,
      )}
      icon={
        <InputGroupButton
          size="icon-xs"
          variant="ghost"
          aria-label={revealed ? "Hide value" : "Show value"}
          onClick={() => {
            setRevealed(!revealed)
          }}
        >
          <HugeiconsIcon
            icon={revealed ? ViewOffSlashIcon : ViewIcon}
            strokeWidth={2}
          />
        </InputGroupButton>
      }
    />
  )
}
